import * as _ from 'lodash'
import {BehaviorSubject} from 'rxjs'
import {ASync} from '../PA.Core/Basic/ASync'
import * as TPMConstants from '../PA.TPM.Core/TPM.Constants'

import {EventBO} from "../PA.TPM.BusinessObjects/EventBO";
import {Subscription} from "rxjs/Subscription";
import {IEventStandardCalculator} from "./PA.TPM.Services.IF";
import {IMessageProvider, IProgressProvider} from "../PA.Core/PA.Core.IF.Basic";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {
    IEventSpendChangedEvent
} from "../PA.TPM.Core/PA.TPM.IF.Aspects";
import {
    WB_EventSpendPrdGrpRow
} from "../PA.Core/PAdbSchemaRows";
import {ExecutionSerializer} from "../PA.Core/Basic/ExecutionSerializer"
import {NumberUtils} from "../PA.Core/Basic/Numbers";


const bDebugSerializers = true

interface ICalcStepOptions {
    bUseQueueing?: boolean,
    bUseProgressProvider?: boolean,
    bUseMessageProvider?: boolean

}

const CalcStepDefaults: ICalcStepOptions = {
    bUseQueueing: false,
    bUseProgressProvider: false,
    bUseMessageProvider: false
}


export class EventStandardCalculator implements IEventStandardCalculator{

    private _enabled: boolean
    private anEventBO: EventBO
    private _CalcProgressProvider: IProgressProvider;
    private _CalcMessageProvider: IMessageProvider;
    private _OnCalculateComplete: BehaviorSubject<IEventStandardCalculator>;

    private _EventSPGChangeSubscription: Subscription
    private anEventChangedRow: WB_EventSpendPrdGrpRow
    // For Each Entry point, we need a serializer

    private qsEvent_lcl_RatePerUnitCalc: ExecutionSerializer

    constructor(options?: any) {

        this._enabled = true
        this.anEventBO = undefined
        this.anEventChangedRow = undefined
        this._CalcMessageProvider = new MessageProvider()
        this._CalcProgressProvider = new ProgressProvider()
        this._OnCalculateComplete = new BehaviorSubject<IEventStandardCalculator>(undefined)

        this._EventSPGChangeSubscription = undefined

        this.qsEvent_lcl_RatePerUnitCalc = new ExecutionSerializer(this, this.Calculate_Rate_Per_Unit.bind(this),
            [{
                bUseQueueing: true,
                bUseProgressProvider: false,
                bUseMessageProvider: false
            }],250, bDebugSerializers)

    }

    Register(pEventBO: EventBO): Promise<void> {
        const self = this
        self.UnRegister()

        if(_.isEmpty(pEventBO)) {
            return ASync.resolve()
        }

        return new Promise<void>((resolve, error) => {

            self.anEventBO = pEventBO

            self._EventSPGChangeSubscription = self.anEventBO.SpendPropertyChanged.subscribe(self.OnEventSPGChange.bind(self))
            resolve()
        })

    }

    UnRegister(){
        if(this._EventSPGChangeSubscription) {
            this._EventSPGChangeSubscription.unsubscribe()
            this._EventSPGChangeSubscription = undefined
        }

        this.anEventBO = undefined
        this.anEventChangedRow = undefined

    }

    get Enabled(): boolean {
        return this._enabled
    }

    set Enabled(value) {
        this._enabled = value
    }

    get Event(): EventBO { return this.anEventBO }

    get CalcProgressProvider(): IProgressProvider { return this._CalcProgressProvider }
    get CalcMessageProvider(): IMessageProvider { return this._CalcMessageProvider }
    get OnCalculateComplete(): BehaviorSubject<IEventStandardCalculator> { return this._OnCalculateComplete }


    // Change Events

    private OnEventSPGChange(oEvent: IEventSpendChangedEvent) {
        const self = this
        if(_.isEmpty(oEvent)) { return }

        self.anEventChangedRow = oEvent.Row

        //oEvent.EventBO.WB_Event.WB_EventSpend_ProductGroupRows[0].WB_EventSpendPrdGrpOverrideRow.cif.
        switch (oEvent.PropertyChanged) {
            // Attributes that trigger Spend Calc
            case 'RATE_UOM':
            case 'RATE_AMT':
            case 'WB_EventSpendPrdGrpOverrideRow.RATE_AMT_OVRD':
                this.qsEventLclRatePerUnitCalc(oEvent.PropertyChanged)
                break

        }

    }

    private qsEventLclRatePerUnitCalc(reason?: string){
        this.qsEvent_lcl_RatePerUnitCalc.Run({sReason: reason})
    }

    private Calculate_Rate_Per_Unit(options?: ICalcStepOptions): Promise<void> {
        const self = this
        let opts: ICalcStepOptions = {}
        _.defaults(opts, options || {}, CalcStepDefaults)

        if (_.isEmpty(this.anEventBO)) {
            return ASync.resolve()
        }

        return new Promise<void>((resolve, error) => {
            EventStandardCalculator.Event_calculateRatePerUnit(self.anEventBO, self.anEventChangedRow)
                .then((ignore) => {
                resolve()
            }).catch((err) => {
                if (opts.bUseProgressProvider) {
                    self.CalcProgressProvider.exception(err)
                }
                error(err)
            })
        })
    }

    static Event_calculateRatePerUnit(anEvent: EventBO, anESPGRow: WB_EventSpendPrdGrpRow): Promise<void> {

        return new Promise<void>((resolve, error) => {
            EventStandardCalculator.EventSPG_calculateRatePerUnit(anEvent, anESPGRow)
            resolve()
        })
    }

  static EventSPG_calculateRatePerUnit(evt: EventBO, espg: WB_EventSpendPrdGrpRow): void {

    let aRate = (espg.WB_EventSpendPrdGrpOverrideRow.cif.RATE_AMT_OVRD > 0) ? espg.WB_EventSpendPrdGrpOverrideRow.cif.RATE_AMT_OVRD : espg.cif.RATE_AMT
    let uom = espg.cif.RATE_UOM

    let newRate = 0
    let bUpdate = false

    switch (true) {

      case _.includes(TPMConstants.Spend_Basis_Fixed, espg.cif.RATE_BASIS_ID):
        newRate = 0
        bUpdate = true
        break;

      case _.includes(TPMConstants.Spend_UOM_Units, uom):
          newRate = aRate
          bUpdate = true
        break;

      case _.includes(TPMConstants.Spend_UOM_ListSales, uom):
          newRate = aRate
          bUpdate = true
        break;

      case _.includes(TPMConstants.Spend_UOM_Case, uom):
        if (espg.WB_ProductGroupRow.cif.CASE_PACK_SIZE > 0) {
            newRate = aRate / espg.WB_ProductGroupRow.cif.CASE_PACK_SIZE
            bUpdate = true

        } else {
            newRate = 0
            bUpdate = true
        }
        break;

      case _.includes(TPMConstants.Spend_UOM_Weight, uom):
        if (espg.WB_ProductGroupRow.cif.WEIGHT_FACTOR > 0) {
            newRate = aRate / espg.WB_ProductGroupRow.cif.WEIGHT_FACTOR
            bUpdate = true
        } else {
            newRate = 0
            bUpdate = true
        }
        break;

    }

    // Avoid triggering recalc due to rate recalc in Actual Spend calculator
      newRate = NumberUtils.round(newRate, 4)
    if(bUpdate && (espg.cif.RATE_AMT_PER_UNIT != newRate))
        espg.cif.RATE_AMT_PER_UNIT = newRate
  }

}